// The component owns only an opaque token. Generated credentials remain in memory,
// outside React/Redux, browser storage, logs, and analytics.
const attempts = new WeakMap();
let activeAttempt = null;

export const createGuestCheckoutAttempt = () => {
  const attempt = Object.freeze({});
  attempts.set(attempt, { disposed: false, inFlight: null, creation: 'new', synced: false });
  return attempt;
};

export const clearGuestCheckoutAttempt = attempt => {
  const state = attempt && attempts.get(attempt);
  if (state) {
    state.disposed = true;
    state.password = null;
    state.email = null;
    state.firstName = null;
    state.lastName = null;
    state.userId = null;
  }
};

const normalizeEmail = email =>
  String(email || '')
    .trim()
    .toLowerCase();
const userFromResponse = response => response?.data?.data;
const userId = user => user?.id?.uuid || user?.id;
const errorStatus = error => Number(error?.status || error?.statusCode || error?.response?.status);
const isUnauthenticated = error => errorStatus(error) === 401;
const isForbidden = error =>
  errorStatus(error) === 403 && (error?.data?.errors || []).some(e => e?.code === 'forbidden');
const isDefinitiveCreateRejection = error => {
  const status = errorStatus(error);
  return status >= 400 && status < 500 && status !== 408 && status !== 429;
};
const isEmailConflict = error =>
  (error?.data?.errors || []).some(e => e?.code === 'email-taken') || errorStatus(error) === 409;

const failed = (stage, reason, retryable = true, requiresLogin = false) => ({
  ok: false,
  stage,
  reason,
  retryable,
  requiresLogin,
  // Never return an SDK error: it can carry a request body with credentials/PII.
  error: new Error(`Guest checkout ${stage} failed: ${reason}`),
});

/**
 * Resume one mounted checkout's guest authentication without hydrating Redux's
 * currentUser (that could unmount its live Stripe Card Element).
 *
 * Dependencies are explicit for hermetic tests. showCurrentUser reads the current
 * SDK session without dispatching a currentUser update. A successful login alone
 * is not enough: identity is verified before sync and before returning ready.
 */
export const runGuestCheckoutAttempt = (attempt, params, dependencies) => {
  const state = attempt && attempts.get(attempt);
  if (!state || state.disposed) {
    return Promise.resolve(failed('validation', 'attempt_expired', false));
  }
  const email = normalizeEmail(params?.email);
  const firstName = String(params?.firstName || '').trim();
  const lastName = String(params?.lastName || '').trim();
  if (!email || !firstName || !lastName || (!state.password && !params?.password)) {
    return Promise.resolve(failed('validation', 'missing_fields', false));
  }
  if (state.email && state.email !== email) {
    if (state.creation !== 'new' || state.inFlight) {
      return Promise.resolve(failed('identity', 'identity_changed', false, true));
    }
    state.email = null;
    state.password = null;
  }
  if (state.inFlight) return state.inFlight;
  if (activeAttempt || dependencies.authenticationInProgress()) {
    return Promise.resolve(failed('authentication', 'authentication_in_progress'));
  }
  if (!state.email) {
    state.email = email;
    state.firstName = firstName;
    state.lastName = lastName;
    state.password = params.password;
  }

  const { createUser, login, showCurrentUser, syncPromotix, onStage = () => {} } = dependencies;
  const cancelled = () => (state.disposed ? failed('validation', 'attempt_expired', false) : null);
  const matches = user =>
    normalizeEmail(user?.attributes?.email) === state.email &&
    (!state.userId || String(userId(user)) === String(state.userId));
  const readIdentity = async () => {
    try {
      const user = userFromResponse(await showCurrentUser());
      return user?.id ? { user } : { failure: failed('identity', 'identity_unavailable') };
    } catch (error) {
      if (isUnauthenticated(error)) return { user: null };
      // Sharetribe rejects anonymous public-read tokens with 403/forbidden.
      // Confirm the SDK's actual token scope; other forbidden sessions stay blocked.
      if (isForbidden(error) && typeof dependencies.readAuthInfo === 'function') {
        try {
          const authInfo = await dependencies.readAuthInfo();
          if (authInfo?.isAnonymous === true) return { user: null };
        } catch (_) {
          // A failed scope read cannot establish that the visitor is anonymous.
        }
      }
      return { failure: failed('identity', 'identity_check_failed') };
    }
  };
  const create = async () => {
    onStage('create', 'start');
    state.creation = 'uncertain';
    try {
      const response = await createUser({
        email: state.email,
        password: state.password,
        firstName: state.firstName,
        lastName: state.lastName,
      });
      if (!state.disposed) {
        state.creation = 'confirmed';
        state.userId = userId(userFromResponse(response)) || null;
      }
      onStage('create', 'success');
      return null;
    } catch (error) {
      const definitive = isDefinitiveCreateRejection(error);
      state.creation = definitive ? 'rejected' : 'uncertain';
      const result = failed(
        'create',
        isEmailConflict(error)
          ? 'email_exists'
          : definitive
          ? 'signup_rejected'
          : 'signup_unconfirmed',
        !definitive,
        isEmailConflict(error)
      );
      state.createFailure = result;
      onStage('create', 'failure', result);
      return result;
    }
  };
  const authenticate = async () => {
    onStage('login', 'start');
    try {
      await login({ username: state.email, password: state.password });
      onStage('login', 'success');
      return null;
    } catch (error) {
      const result = failed('login', 'login_failed');
      onStage('login', 'failure', result);
      return { ...result, unauthenticated: isUnauthenticated(error) };
    }
  };

  activeAttempt = attempt;
  dependencies.onFlowStart?.();
  state.inFlight = Promise.resolve().then(async () => {
    try {
      if (state.creation === 'rejected') return state.createFailure;
      let identity = await readIdentity();
      if (cancelled()) return cancelled();
      if (identity.failure) return identity.failure;
      if (dependencies.authenticationInProgress())
        return failed('authentication', 'authentication_in_progress');
      if (identity.user) {
        // Even the same email is not proof of ownership for a fresh attempt.
        if (state.creation !== 'confirmed' || !matches(identity.user)) {
          return failed('identity', 'identity_changed', false, true);
        }
      } else {
        if (state.creation === 'new') {
          const createFailure = await create();
          if (cancelled()) return cancelled();
          if (createFailure) return createFailure;
        }
        // When the create response was lost, the retained random password can
        // recover only that attempt's account. Never try a new password or bypass login.
        const uncertainCreate = state.creation === 'uncertain';
        let loginFailure = await authenticate();
        if (cancelled()) return cancelled();
        if (loginFailure && uncertainCreate && loginFailure.unauthenticated) {
          // A definitive bad-credentials response can mean create never reached
          // the server. Retrying with the same identity/password is safe; a real
          // existing account still rejects create and requires normal Login.
          const createFailure = await create();
          if (cancelled()) return cancelled();
          if (createFailure) return createFailure;
          loginFailure = await authenticate();
          if (cancelled()) return cancelled();
        }
        if (loginFailure) return failed('login', loginFailure.reason);
        identity = await readIdentity();
        if (cancelled()) return cancelled();
        if (identity.failure) return identity.failure;
        if (!identity.user || !matches(identity.user)) {
          return failed('identity', 'identity_changed', false, true);
        }
        state.creation = 'confirmed';
      }
      state.userId = userId(identity.user);
      if (!state.synced) {
        onStage('sync', 'start');
        try {
          await syncPromotix();
          if (!state.disposed) state.synced = true;
          onStage('sync', 'success');
        } catch (error) {
          const result = failed('sync', 'promotix_sync_failed');
          onStage('sync', 'failure', result);
          return result;
        }
      }
      if (cancelled()) return cancelled();
      // Detect logout / a different user logging in while a dependency was pending.
      identity = await readIdentity();
      if (cancelled()) return cancelled();
      if (identity.failure) return identity.failure;
      if (!identity.user || !matches(identity.user)) {
        return failed('identity', 'identity_changed', false, true);
      }
      if (dependencies.authenticationInProgress()) {
        return failed('authentication', 'authentication_in_progress');
      }
      return { ok: true, stage: 'ready', reason: 'ready', retryable: false, requiresLogin: false };
    } finally {
      state.inFlight = null;
      if (activeAttempt === attempt) activeAttempt = null;
      dependencies.onFlowEnd?.();
    }
  });
  return state.inFlight;
};
